import { useTranslation } from "next-i18next";
import React, { FC, useMemo } from "react";

import { isClearpay } from "@/utils/locale";
import { ONETRUST_FOOTER_CTA_NAME } from "@/utils/onetrust";

import { UI_CONTEXTS } from "@/analytics/constants";
import EVENTS from "@/analytics/events";

import {
    KINDS as BUTTON_KINDS,
    SIZES as BUTTON_SIZES,
} from "@/components/buttons/buttons.constants";
import LinkButton from "@/components/buttons/link-button";
import { ApLogo } from "@/components/icons/ap-logo";

import { useIsArcade } from "@/hooks/useIsArcade";
import { useShowSpecificGiftCardLine } from "@/hooks/useIsGiftCards";

import { GenericNavItem } from "@/interfaces/navigation-interface";

import { Locale } from "../../types/locale";
import { ArcadeFooterRegional } from "./arcade/footer-regional";
import styles from "./footer.module.scss";
import { OnetrustSettingsItem } from "./onetrust-footer-item";

type Props = {
    navigationItems: Array<Array<GenericNavItem>>;
    currentLocale: Locale;
};

const APFooterRegional: React.FC<Props> = ({ navigationItems, currentLocale }) => {
    const { t } = useTranslation();

    const showSpecificGiftCardLine = useShowSpecificGiftCardLine(currentLocale);
    const links = navigationItems.map((chunk: Array<GenericNavItem>, index: number) => (
        <ul className={styles.colSmall} key={index}>
            {chunk?.map((chunkItem: GenericNavItem) => {
                if (ONETRUST_FOOTER_CTA_NAME === chunkItem.text) {
                    return <OnetrustSettingsItem currentLocale={currentLocale} />;
                }
                return (
                    <li key={chunkItem.id}>
                        <LinkButton
                            url={chunkItem.link}
                            label={chunkItem.text}
                            kind={BUTTON_KINDS.UNSET}
                            size={BUTTON_SIZES.UNSET}
                            passHref={true}
                            analytics={{
                                eventProps: {
                                    footerLink: chunkItem.link,
                                    footerLinkText: chunkItem.text,
                                },
                                eventName: EVENTS.FOOTER_LINK_CLICK,
                                uiContext: UI_CONTEXTS.FOOTER_NAVIGATION,
                            }}
                        />
                    </li>
                );
            })}
        </ul>
    ));

    const legalTextContent1 = t("footer.legalText.line1"),
        legalTextContent2 = t("footer.legalText.line2"),
        legalTextContent3 = t("footer.legalText.line3");
    const legalText = useMemo(
        () =>
            legalTextContent1 && (
                <div className={styles.legalText}>
                    <p dangerouslySetInnerHTML={{ __html: legalTextContent1 }}></p>
                    <br />
                    <p dangerouslySetInnerHTML={{ __html: legalTextContent2 }}></p>
                    {legalTextContent3 && (
                        <>
                            <br />
                            <p dangerouslySetInnerHTML={{ __html: legalTextContent3 }}></p>
                        </>
                    )}
                </div>
            ),
        [legalTextContent1, legalTextContent2, legalTextContent3]
    );

    const acknowledgementText = useMemo(() => {
        return (
            <div className={styles.acknowledgementText}>
                {showSpecificGiftCardLine ? (
                    <p>{t("footer.acknowledgementText.giftCardSpecificLine1")}</p>
                ) : isClearpay(currentLocale) ? (
                    <>
                        <p>{t("footer.acknowledgementText.creditTermsSummary")}</p>
                        <br />
                        <p>{t("footer.acknowledgementText.regulatoryStatement")}</p>
                    </>
                ) : (
                    <p>{t("footer.acknowledgementText.line1")}</p>
                )}

                <br />
                <p>
                    {t("footer.acknowledgementText.line2")}
                    <b>{t("footer.acknowledgementText.line3")}</b>
                </p>
            </div>
        );
    }, [currentLocale, showSpecificGiftCardLine, t]);

    return (
        <>
            {links}
            {legalText}
            {acknowledgementText}
            <div className={styles.logo}>
                <ApLogo />
            </div>
        </>
    );
};

export const FooterRegional: FC<Props> = (props) => {
    const isArcade = useIsArcade();
    return isArcade ? <ArcadeFooterRegional {...props} /> : <APFooterRegional {...props} />;
};
